import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ClipboardCheck, MapPin, Clock, Users } from 'lucide-react';
import { API_URL } from '@/config/api';

const MARK = {
  present: 'bg-green-100 text-green-700', late: 'bg-amber-100 text-amber-700',
  absent: 'bg-red-100 text-red-700', excused: 'bg-slate-200 text-slate-600',
};

// Opened from the calendar: one class on one date, with its students and that day's register.
const ClassDetailDialog = ({ cls, onClose, onOpenStudent }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!cls) return;
    setData(null);
    setError(false);
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/training/courses/${cls.course_id}/register`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include',
    })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, [cls]);

  if (!cls) return null;
  const marks = data?.records?.[cls.date] || {};
  const session = data?.sessions?.find(s => s.date === cls.date);
  const isFuture = session?.when === 'upcoming';

  return (
    <Dialog open={!!cls} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{cls.course_name}</DialogTitle>
          <DialogDescription>
            {new Date(`${cls.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-3 text-sm text-slate-600">
          <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-slate-400" /> {cls.time}</span>
          {cls.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-slate-400" /> {cls.location}</span>}
          {cls.accreditation && <Badge className="bg-blue-50 text-blue-600">{cls.accreditation}</Badge>}
        </div>

        {error ? (
          <p className="text-sm text-red-600 py-4">Couldn't load this class.</p>
        ) : !data ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 text-pink-400 animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-slate-50 p-2">
                <p className="text-lg font-bold text-slate-900">{data.students.length}</p>
                <p className="text-[11px] text-slate-500">Students</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-2">
                <p className="text-lg font-bold text-slate-900">{data.summary.average_attendance != null ? `${data.summary.average_attendance}%` : '—'}</p>
                <p className="text-[11px] text-slate-500">Avg attendance</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-2">
                <p className="text-lg font-bold text-slate-900">
                  {isFuture ? '—' : `${(session?.counts?.present || 0) + (session?.counts?.late || 0)}/${data.students.length}`}
                </p>
                <p className="text-[11px] text-slate-500">{isFuture ? 'Upcoming' : 'In this class'}</p>
              </div>
            </div>
            <div>
              <p className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                <Users className="w-3.5 h-3.5" /> Students
              </p>
              {data.students.length === 0 ? (
                <p className="text-sm text-slate-400 py-3">No students enrolled yet.</p>
              ) : (
                <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
                  {data.students.map(st => {
                    const m = marks[st.enrollment_id];
                    return (
                      <div key={st.enrollment_id} className="flex items-center justify-between gap-2 px-3 py-2">
                        <div className="min-w-0">
                          <button type="button" onClick={() => onOpenStudent?.(st.lead_id)} className="text-sm font-medium text-slate-800 truncate hover:text-pink-600 text-left">{st.name}</button>
                          <p className="text-xs text-slate-500">
                            {st.stats.percentage != null ? `${st.stats.percentage}% attendance` : 'No classes counted yet'}
                            {st.phone ? ` · ${st.phone}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {st.at_risk && <Badge className="bg-red-100 text-red-700 text-[11px]">At risk</Badge>}
                          {!isFuture && (
                            <span className={`text-[11px] font-medium rounded-md px-1.5 py-0.5 ${m ? MARK[m.status] : 'bg-slate-50 text-slate-400'}`}>
                              {m ? m.status : 'not marked'}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={onClose}>Close</Button>
          <Button className="btn-primary rounded-xl" onClick={() => navigate(`/courses?register=${cls.course_id}&date=${cls.date}`)}>
            <ClipboardCheck className="w-4 h-4 mr-1" /> Open register
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClassDetailDialog;
